// This file is part of ecbyts/ECBT (Environmental & Occupational Core Byte Tools).
// Copyright (C) 2026 Calvin Stefan Iost
// Licensed under the GNU Affero General Public License v3.0 (AGPL-3.0-only).

/* ================================================================
   BAYES-001 — Tipos canônicos do módulo de avaliação bayesiana.

   BayesObservation é a ÚNICA porta de entrada de dados no motor:
   adapters convertem qualquer dado do modelo (elemento, canal voxel,
   observação de incerteza, saída de score) neste formato normalizado,
   mantendo o motor puro e testável. SPEC_BAYES_001 §4.2/RF4.
   ================================================================ */

export type BayesObservationKind = 'binary' | 'count' | 'continuous' | 'censored';

export interface BayesCensoring {
    readonly type: 'LD' | 'LQ';
    readonly limit: number;
}

export interface BayesObservation {
    readonly sourceId: string;
    readonly domain: string;
    readonly kind: BayesObservationKind;
    readonly value: number;
    readonly n?: number | undefined;
    readonly censoring?: BayesCensoring | undefined;
    readonly uValue?: { abs: number } | undefined;
    readonly ts: string;
}

export type BayesFamily = 'beta' | 'normal' | 'gamma';

export interface BetaPrior {
    readonly family: 'beta';
    readonly a: number;
    readonly b: number;
}
export interface NormalPrior {
    readonly family: 'normal';
    readonly mu: number;
    readonly tau2: number;
}
export interface GammaPrior {
    readonly family: 'gamma';
    readonly alpha: number;
    readonly rate: number;
}
export type PriorSpec = BetaPrior | NormalPrior | GammaPrior;

export interface BayesGrid {
    readonly xs: readonly number[];
    readonly weights: readonly number[];
}

export interface BayesSummaries {
    readonly mean: number;
    readonly median: number;
    readonly sd: number;
    readonly ci95: { readonly lo: number; readonly hi: number };
    readonly hpd95: { readonly lo: number; readonly hi: number };
}

export interface BayesPosteriorResult {
    readonly family: BayesFamily;
    readonly prior: PriorSpec;
    readonly params: Record<string, number>;
    readonly grid: BayesGrid;
    readonly summaries: BayesSummaries;
    readonly nObs: number;
    readonly updatedAt: string;
}

export const BAYES_ERROR_CODES = Object.freeze({
    EMPTY_INPUT: 'BAYES_EMPTY_INPUT',
    CROMWELL: 'BAYES_CROMWELL',
    KIND_MISMATCH: 'BAYES_KIND_MISMATCH',
    INVALID_OBSERVATION: 'BAYES_INVALID_OBSERVATION',
});

const KINDS: ReadonlyArray<BayesObservationKind> = ['binary', 'count', 'continuous', 'censored'];

export class BayesError extends Error {
    readonly code: string;
    constructor(code: string, message: string) {
        super(message);
        this.name = 'BayesError';
        this.code = code;
    }
}

export function isBayesObservation(o: unknown): boolean {
    if (!o || typeof o !== 'object') return false;
    const x = o as Record<string, unknown>;
    if (typeof x.sourceId !== 'string' || !x.sourceId) return false;
    if (typeof x.domain !== 'string' || !x.domain) return false;
    if (!KINDS.includes(x.kind as BayesObservationKind)) return false;
    if (typeof x.value !== 'number' || !Number.isFinite(x.value)) return false;
    if (x.ts !== undefined && typeof x.ts !== 'string') return false;
    if (x.n !== undefined && (!Number.isFinite(x.n) || (x.n as number) <= 0)) return false;
    return true;
}

/**
 * Valida observações em lote — fail-closed: lista vazia é erro explícito
 * (Empty gates fail closed, AGENTS.md), item malformado aponta o índice.
 */
export function validateObservations(
    observations: ReadonlyArray<unknown>,
): asserts observations is ReadonlyArray<BayesObservation> {
    if (!Array.isArray(observations) || observations.length === 0) {
        throw new BayesError(BAYES_ERROR_CODES.EMPTY_INPUT, 'Nenhuma observação foi extraída (empty input).');
    }
    observations.forEach((o, i) => {
        if (!isBayesObservation(o)) {
            throw new BayesError(BAYES_ERROR_CODES.INVALID_OBSERVATION, `Observação inválida no índice ${i}.`);
        }
    });
}

/**
 * Cromwell's rule (Jaynes 2003): prior com massa 0 ou 1 nunca atualiza —
 * graus de crença extremos são rejeitados na fronteira do motor.
 */
export function validatePrior(prior: PriorSpec): void {
    if (!prior || typeof prior !== 'object') {
        throw new BayesError(BAYES_ERROR_CODES.CROMWELL, 'Prior ausente.');
    }
    const p = prior as unknown as Record<string, unknown>;
    const pos = (v: unknown) => typeof v === 'number' && Number.isFinite(v) && (v as number) > 0;
    if (p.family === 'beta' && pos(p.a) && pos(p.b)) return;
    if (p.family === 'normal' && Number.isFinite(p.mu as number) && pos(p.tau2)) return;
    if (p.family === 'gamma' && pos(p.alpha) && pos(p.rate)) return;
    throw new BayesError(BAYES_ERROR_CODES.CROMWELL, `Prior inválida p/ família ${String(p.family)} (Cromwell).`);
}

export function familyForKind(kind: BayesObservationKind): BayesFamily {
    if (kind === 'binary') return 'beta';
    if (kind === 'count') return 'gamma';
    return 'normal';
}
